import {
  ExternalLocationHubConfig,
  ExternalReturnHubType,
} from "@redotech/account-common/address";
import { findCountryCode } from "@redotech/locale/countries";
import { returnsCenterIdFormat } from "@redotech/redo-ids/ids";
import {
  DEFAULT_INVENTORY_PUSH_MINIMUM_QUANTITY,
  getInventoryPushMinimumQuantity,
} from "@redotech/redo-model/inventory/inventory-push-buffer";
import {
  defaultReceivingLabelField,
  ReceivingLabelField,
} from "@redotech/redo-model/inventory/receiving-label";
import { Team } from "@redotech/redo-model/team";
import {
  GradeAcceptance,
  type GradeMapping,
} from "@redotech/redo-return-settings-common/grade-mapping";
import {
  DEFAULT_RETURNS_PROCESSING_COST,
  type redoManagedReturnsSettings,
} from "@redotech/redo-return-settings-common/redo-managed-returns-settings";
import { RemitMode } from "@redotech/redo-return-settings-common/remit-mode";
import { ReturnProcessingLevel } from "@redotech/redo-return-settings-common/return-processing-level";
import {
  ConsolidationShipToAddress,
  ReturnsCenterConfig,
} from "@redotech/redo-return-settings-common/returns-center-config";
import { groupInput, input, InputSchema } from "@redotech/ui/form";
import { numberValidator } from "@redotech/ui/validators";
import { deepIntersectionEquals } from "@redotech/util/equal";

export interface ReturnsCenterConfigFormValue {
  returnsCenterId: string;
  consolidationScheduleDays: string;
  shipToName: string;
  shipToStreet1: string;
  shipToStreet2: string;
  shipToCity: string;
  shipToState: string;
  shipToZip: string;
  shipToCountry: string;
  refulfillment: boolean;
  processingFee: string;
}

export interface ExternalLocationHubFormConfig {
  provider: ExternalReturnHubType | "";
  locationId: string;
}

function formatCents(value: number): string {
  return value.toFixed(2);
}

function formatCentsOptional(value: number | undefined | null): string {
  if (value == null || value === 0) {
    return "";
  }
  return value.toFixed(2);
}

function formatCoverageSharePercent(value: number | undefined | null): string {
  if (value == null || value === 0) {
    return "";
  }
  return Number((value * 100).toFixed(6)).toString();
}

function parseCoverageSharePercent(value: string): number {
  const numberValue = Number(value);
  return isNaN(numberValue) ? 0 : numberValue / 100;
}

function coverageSharePercentValidator(value: {
  processingCoverageSharePercent: string;
  consolidationCoverageSharePercent: string;
}): string[] {
  const total =
    (Number(value.processingCoverageSharePercent) || 0) +
    (Number(value.consolidationCoverageSharePercent) || 0);
  if (total > 100) {
    return ["Total coverage percentage cannot exceed 100%."];
  }
  return [];
}

function validateInventoryPushMinimumQuantity(value: string): string[] {
  const trimmed = value.trim();
  const parsed = Number(trimmed);
  if (!/^\d+$/.test(trimmed) || !Number.isSafeInteger(parsed)) {
    return ["Enter a whole number of 0 or more."];
  }
  return [];
}

function parseInventoryPushMinimumQuantity(value: string): number {
  const errors = validateInventoryPushMinimumQuantity(value);
  if (errors.length > 0) {
    return DEFAULT_INVENTORY_PUSH_MINIMUM_QUANTITY;
  }
  return Number(value);
}

export const availableReturnsCentersInput = input<
  ReturnsCenterConfigFormValue[]
>({ equal: deepIntersectionEquals });

export const redoManagedReturnsSettingsForm = groupInput(
  {
    enabled: input<boolean>(),
    processingCost: input<string>({ validator: numberValidator({ min: 0 }) }),
    processingCoverageSharePercent: input<string>({
      validator: numberValidator({ min: 0, max: 100 }),
    }),
    consolidationCoverageSharePercent: input<string>({
      validator: numberValidator({ min: 0, max: 100 }),
    }),
    returnProcessingLevel: input<ReturnProcessingLevel>(),
    remitMode: input<RemitMode>(),
    verificationPassOutcome: input<GradeAcceptance>(),
    verificationFailOutcome: input<GradeAcceptance>(),
    inventoryPushMinimumQuantity: input<string>({
      validator: validateInventoryPushMinimumQuantity,
    }),
    receivingLabelEnabled: input<boolean>(),
    receivingLabelEncodedField: input<ReceivingLabelField>(),
    externalLocationHubMappings: input<
      Record<string, ExternalLocationHubFormConfig>
    >({ equal: deepIntersectionEquals }),
    availableReturnsCenters: availableReturnsCentersInput,
    gradeMappings: input<GradeMapping[]>({ equal: deepIntersectionEquals }),
  },
  { validator: coverageSharePercentValidator },
);

export type RedoManagedReturnsSettingsForm = InputSchema.Input<
  typeof redoManagedReturnsSettingsForm
>;
export type RedoManagedReturnsSettingsValue = InputSchema.Value<
  typeof redoManagedReturnsSettingsForm
>;

export const redoManagedReturnsSettingsDefault: RedoManagedReturnsSettingsValue =
  {
    enabled: false,
    processingCost: `${DEFAULT_RETURNS_PROCESSING_COST}`,
    processingCoverageSharePercent: "",
    consolidationCoverageSharePercent: "",
    returnProcessingLevel: ReturnProcessingLevel.VERIFICATION,
    remitMode: RemitMode.AUTOMATIC,
    verificationPassOutcome: GradeAcceptance.ACCEPT,
    verificationFailOutcome: GradeAcceptance.FLAG_FOR_REVIEW,
    inventoryPushMinimumQuantity: `${DEFAULT_INVENTORY_PUSH_MINIMUM_QUANTITY}`,
    receivingLabelEnabled: false,
    receivingLabelEncodedField: defaultReceivingLabelField,
    externalLocationHubMappings: {},
    availableReturnsCenters: [],
    gradeMappings: [],
  };

export const redoManagedReturnsTeamToFormValue = (team: {
  settings: { redoManagedReturns?: redoManagedReturnsSettings };
}): RedoManagedReturnsSettingsValue => {
  const storedMappings =
    team.settings.redoManagedReturns?.externalLocationHubMappings;
  const formMappings: Record<string, ExternalLocationHubFormConfig> = {};
  if (storedMappings) {
    for (const [key, value] of Object.entries(storedMappings)) {
      if (!value) {
        continue;
      }
      // DB stores raw numeric keys; convert to formatted for form display
      const formattedKey =
        key && !returnsCenterIdFormat.test(key)
          ? returnsCenterIdFormat.write(BigInt(key))
          : key;
      formMappings[formattedKey] = {
        provider: value.provider,
        locationId: value.locationId ?? "",
      };
    }
  }

  const storedCenters =
    team.settings.redoManagedReturns?.availableReturnsCenters ?? [];
  const formCenters: ReturnsCenterConfigFormValue[] = storedCenters.map(
    (config) => {
      const formattedCenterId =
        config.returnsCenterId &&
        !returnsCenterIdFormat.test(config.returnsCenterId)
          ? returnsCenterIdFormat.write(BigInt(config.returnsCenterId))
          : config.returnsCenterId;
      return {
        returnsCenterId: formattedCenterId,
        consolidationScheduleDays:
          config.consolidationScheduleDays?.toString() ?? "",
        shipToName: config.shipToAddress?.name ?? "",
        shipToStreet1: config.shipToAddress?.street1 ?? "",
        shipToStreet2: config.shipToAddress?.street2 ?? "",
        shipToCity: config.shipToAddress?.city ?? "",
        shipToState: config.shipToAddress?.state ?? "",
        shipToZip: config.shipToAddress?.zip ?? "",
        shipToCountry:
          findCountryCode(config.shipToAddress?.country ?? "") ??
          config.shipToAddress?.country ??
          "",
        refulfillment: config.refulfillment ?? false,
        processingFee: formatCentsOptional(config.processingFee),
      };
    },
  );

  return {
    enabled: team.settings.redoManagedReturns?.enabled ?? false,
    processingCost: formatCents(
      team.settings.redoManagedReturns?.processingCost ??
        DEFAULT_RETURNS_PROCESSING_COST,
    ),
    processingCoverageSharePercent: formatCoverageSharePercent(
      team.settings.redoManagedReturns?.processingCoverageSharePercent,
    ),
    consolidationCoverageSharePercent: formatCoverageSharePercent(
      team.settings.redoManagedReturns?.consolidationCoverageSharePercent,
    ),
    returnProcessingLevel:
      team.settings.redoManagedReturns?.returnProcessingLevel ??
      ReturnProcessingLevel.VERIFICATION,
    remitMode:
      team.settings.redoManagedReturns?.remitMode ?? RemitMode.AUTOMATIC,
    verificationPassOutcome:
      team.settings.redoManagedReturns?.verificationPassOutcome ??
      GradeAcceptance.ACCEPT,
    verificationFailOutcome:
      team.settings.redoManagedReturns?.verificationFailOutcome ??
      GradeAcceptance.FLAG_FOR_REVIEW,
    inventoryPushMinimumQuantity: getInventoryPushMinimumQuantity(
      team.settings.redoManagedReturns,
    ).toString(),
    receivingLabelEnabled:
      team.settings.redoManagedReturns?.receivingLabel?.enabled ?? false,
    receivingLabelEncodedField:
      team.settings.redoManagedReturns?.receivingLabel?.encodedField ??
      defaultReceivingLabelField,
    externalLocationHubMappings: formMappings,
    availableReturnsCenters: formCenters,
    gradeMappings: team.settings.redoManagedReturns?.gradeMappings ?? [],
  };
};

export const redoManagedReturnsFormValueToTeam = (
  redoManagedReturnsSettings: RedoManagedReturnsSettingsValue,
): Team["settings"]["redoManagedReturns"] => {
  const mappings = redoManagedReturnsSettings.externalLocationHubMappings;
  const hubMappings: Record<string, ExternalLocationHubConfig> = {};
  if (mappings) {
    for (const [key, value] of Object.entries(mappings)) {
      if (value.provider) {
        // Convert formatted key (rctr_...) back to raw numeric for DB storage
        const rawKey = returnsCenterIdFormat.test(key)
          ? returnsCenterIdFormat.read(key).toString()
          : key;
        hubMappings[rawKey] = {
          provider: value.provider,
          locationId:
            value.locationId && value.locationId.trim().length > 0
              ? value.locationId
              : undefined,
        };
      }
    }
  }

  const formCenters = redoManagedReturnsSettings.availableReturnsCenters ?? [];
  const availableReturnsCenters: ReturnsCenterConfig[] = formCenters.map(
    (c) => {
      const rawCenterId = returnsCenterIdFormat.test(c.returnsCenterId)
        ? returnsCenterIdFormat.read(c.returnsCenterId).toString()
        : c.returnsCenterId;

      const hasAddress = c.shipToName || c.shipToStreet1;
      const shipToAddress: ConsolidationShipToAddress | undefined = hasAddress
        ? {
            name: c.shipToName,
            street1: c.shipToStreet1,
            street2: c.shipToStreet2 || undefined,
            city: c.shipToCity,
            state: c.shipToState || undefined,
            zip: c.shipToZip,
            country: c.shipToCountry,
          }
        : undefined;

      const days = Number(c.consolidationScheduleDays);
      const processingFee = Number(c.processingFee);

      return {
        returnsCenterId: rawCenterId,
        consolidationScheduleDays:
          c.consolidationScheduleDays === "" || isNaN(days) ? undefined : days,
        shipToAddress,
        refulfillment: c.refulfillment ?? undefined,
        processingFee:
          c.processingFee === "" || isNaN(processingFee)
            ? undefined
            : processingFee,
      };
    },
  );

  const processingCost = isNaN(
    Number(redoManagedReturnsSettings.processingCost),
  )
    ? DEFAULT_RETURNS_PROCESSING_COST
    : Number(redoManagedReturnsSettings.processingCost);
  return {
    enabled: redoManagedReturnsSettings.enabled,
    processingFee: processingCost,
    processingCost,
    processingCoverageSharePercent: parseCoverageSharePercent(
      redoManagedReturnsSettings.processingCoverageSharePercent,
    ),
    consolidationCoverageSharePercent: parseCoverageSharePercent(
      redoManagedReturnsSettings.consolidationCoverageSharePercent,
    ),
    returnProcessingLevel:
      redoManagedReturnsSettings.returnProcessingLevel ??
      ReturnProcessingLevel.VERIFICATION,
    remitMode: redoManagedReturnsSettings.remitMode ?? RemitMode.AUTOMATIC,
    verificationPassOutcome:
      redoManagedReturnsSettings.verificationPassOutcome ??
      GradeAcceptance.ACCEPT,
    verificationFailOutcome:
      redoManagedReturnsSettings.verificationFailOutcome ??
      GradeAcceptance.FLAG_FOR_REVIEW,
    inventoryPushMinimumQuantity: parseInventoryPushMinimumQuantity(
      redoManagedReturnsSettings.inventoryPushMinimumQuantity,
    ),
    receivingLabel: {
      enabled: redoManagedReturnsSettings.receivingLabelEnabled,
      encodedField: redoManagedReturnsSettings.receivingLabelEncodedField,
    },
    externalLocationHubMappings:
      Object.keys(hubMappings).length > 0 ? hubMappings : undefined,
    availableReturnsCenters:
      availableReturnsCenters.length > 0 ? availableReturnsCenters : undefined,
    gradeMappings:
      redoManagedReturnsSettings.gradeMappings.length > 0
        ? redoManagedReturnsSettings.gradeMappings
        : undefined,
  };
};
